/**
 * Forwarding table for the OLD homepage's section anchors.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * Until the 2026-09-11 cutover `/` was HomePage.tsx and its nav linked eight
 * in-page anchors: `#caselaw`, `#notes`, `#chat`, `#practice`, `#library`,
 * `#pricing`, `#community`, `#about-section`. `/` is now HomeVariantBPage,
 * which renders NONE of the first seven — the product content those anchors
 * described moved to `/law-school/unlimited`.
 *
 * Those links are IN THE WILD and are not all ours to fix:
 *   - cached blog pages and the Astro blog's own nav (absolute
 *     `https://jdsimplified.com/#caselaw` links, rewritten in the same commit
 *     but cached by readers and by the CDN);
 *   - the SPA 404 page's standalone nav copy;
 *   - the pre-cutover SEO baseline's inbound-anchor list;
 *   - anything anyone ever pasted into an email, a Discord message, or a doc.
 *
 * An unrecognised fragment is not an error — the browser simply finds no
 * element and leaves the visitor at the top of a page that looks nothing like
 * what the link promised. That is the quiet failure this table prevents.
 *
 * ── WHY A MODULE AND NOT FOUR LINES IN A useEffect ─────────────────────────
 * So it can be tested without mounting the page, and so the mapping is one
 * enumerable object rather than a chain of conditionals that someone extends by
 * adding another `else if`. See
 * src/lib/marketing/__tests__/legacy-home-anchors.test.ts.
 *
 * ── THIS IS A CLIENT-SIDE CONVENIENCE, NOT AN SEO MECHANISM, AND THE
 *    DISTINCTION MATTERS ──────────────────────────────────────────────────
 * A URL FRAGMENT IS NEVER SENT TO THE SERVER. `GET /#caselaw` is
 * `GET /` on the wire, so no load-balancer rule, no 301, and no server-side
 * redirect can act on these — this is the only layer that can. It is also why
 * there is nothing to add to infra/b1-url-map-live.yaml for them, and why
 * "the 301 will handle it" is wrong here in a second, different way from the
 * client-side-navigation sense it is usually wrong in.
 *
 * Equally: Google does not index fragments separately, so nothing here is
 * recovering link equity. It is recovering a HUMAN who clicked a link.
 */

/**
 * Old-homepage fragment → where that content lives now.
 *
 * Every value is a path PLUS fragment, and every fragment is an id
 * LawSchoolUnlimitedPage really renders (top, problem, practice, library,
 * caselaw, notes, chat, pricing, faq). The test asserts that cross-file, so a
 * section rename over there turns this red rather than silently restoring the
 * dead-anchor behaviour this table exists to end.
 */
export const LEGACY_HOME_ANCHOR_TARGETS: Readonly<Record<string, string>> = {
  caselaw: '/law-school/unlimited#caselaw',
  notes: '/law-school/unlimited#notes',
  chat: '/law-school/unlimited#chat',
  practice: '/law-school/unlimited#practice',
  library: '/law-school/unlimited#library',
  pricing: '/law-school/unlimited#pricing',
};

/**
 * Fragments that are RETIRED WITH NO DESTINATION — forward to bare `/`.
 *
 * `community` is the only one, and it is deliberate rather than an oversight.
 * There is no public community page: `grep -c 'path="/community"' src/App.tsx`
 * returns 0, the feature is flag-gated (`community-board`, production OFF), and
 * the founder's 2026-09-11 ruling is that the in-app sidebar is the surface
 * that reaches it — not a marketing link. Sending this fragment to
 * `/law-school#community` would work today (LawSchoolStagePage renders that id)
 * and would be the wrong answer: it advertises a feature a production visitor
 * cannot reach.
 */
export const RETIRED_HOME_ANCHORS: readonly string[] = ['community'];

/**
 * Fragments `/` still owns. Left alone — HomeVariantBPage renders both.
 *
 * Enumerated rather than implied: without this list the obvious implementation
 * ("anything not in the table is fine") is indistinguishable from the
 * implementation that silently drops a typo, and `#top`'s target is the hero
 * section on EVERY marketing page, so it is the fragment most likely to arrive
 * from somewhere unexpected.
 */
export const HOME_OWNED_ANCHORS: readonly string[] = ['top', 'about-section'];

/**
 * Where a fragment arriving on `/` should send the visitor.
 *
 * @param hash the fragment, with or without a leading '#'
 * @returns a path to replace-navigate to, or `null` to stay put
 *
 * Returns `null` — stay put — for anything unrecognised. That is the
 * conservative direction on purpose: an unknown fragment might belong to a
 * future section of this page, and leaving the visitor on `/` is a worse
 * outcome than a forward only if we are CERTAIN where it went. We are not.
 */
export function resolveLegacyHomeAnchor(hash: string | null | undefined): string | null {
  if (!hash) return null;
  const id = hash.replace(/^#/, '');
  if (!id) return null;
  if (HOME_OWNED_ANCHORS.includes(id)) return null;
  if (RETIRED_HOME_ANCHORS.includes(id)) return '/';
  return LEGACY_HOME_ANCHOR_TARGETS[id] ?? null;
}
